"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { InventoryTransferRecord } from "@/lib/types";

function formatPartErrorMessage(message?: string): string {
  if (!message) return "—";

  const dashSplit = message.split(" — ");
  if (dashSplit.length > 1) {
    return dashSplit[dashSplit.length - 1].trim();
  }

  return message;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "ready") {
    return (
      <Badge className="border-transparent bg-green-600 text-white hover:bg-green-600">
        Ready
      </Badge>
    );
  }

  if (status === "issue") {
    return (
      <Badge variant="destructive" className="border-transparent">
        Issue
      </Badge>
    );
  }

  return <Badge variant="outline">{status}</Badge>;
}

function PartsDetail({ record }: { record: InventoryTransferRecord }) {
  if (!record.parts.length) {
    return (
      <p className="text-sm text-muted-foreground py-2 px-4">
        No parts listed for this transfer.
      </p>
    );
  }

  return (
    <div className="px-4 py-3 bg-muted/40">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Parts ({record.parts.length})
      </p>
      <div className="rounded-md border bg-background overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>UOM</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="min-w-[280px]">Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {record.parts.map((part, index) => (
              <TableRow key={`${part.ItemNo}-${index}`}>
                <TableCell className="font-medium">{part.ItemNo}</TableCell>
                <TableCell>{part.UOMID}</TableCell>
                <TableCell className="text-right">{part.OriginalQty}</TableCell>
                <TableCell>{part.from_warehouse}</TableCell>
                <TableCell>{part.to_warehouse}</TableCell>
                <TableCell>
                  {part.status === "fail" ? (
                    <Badge variant="destructive" className="border-transparent">
                      Fail
                    </Badge>
                  ) : (
                    <Badge className="border-transparent bg-green-600 text-white hover:bg-green-600">
                      {part.status}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatPartErrorMessage(part.message)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {record.validation_errors.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="font-medium text-destructive mb-1">Validation errors</p>
          <ul className="list-disc list-inside text-muted-foreground space-y-1">
            {record.validation_errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const columns: ColumnDef<InventoryTransferRecord>[] = [
  {
    id: "expand",
    header: () => null,
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => row.toggleExpanded()}
      >
        {row.getIsExpanded() ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle parts</span>
      </Button>
    ),
  },
  {
    accessorKey: "TransferNo",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Transfer No
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="pl-2 font-medium">{row.getValue("TransferNo")}</div>
    ),
  },
  {
    accessorKey: "TRXNO",
    header: "TRX No",
    cell: ({ row }) => <div>{row.getValue("TRXNO")}</div>,
  },
  {
    accessorKey: "TranferDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Date
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div>{row.getValue("TranferDate")}</div>,
  },
  {
    accessorKey: "login_site",
    header: "Site",
    cell: ({ row }) => <div>{row.getValue("login_site")}</div>,
  },
  {
    accessorKey: "SalesmanNo",
    header: "Salesman",
    cell: ({ row }) => <div>{row.getValue("SalesmanNo")}</div>,
  },
  {
    accessorKey: "from_warehouse",
    header: "From WH",
    cell: ({ row }) => <div>{row.getValue("from_warehouse")}</div>,
  },
  {
    accessorKey: "WarehouseID",
    header: "To WH",
    cell: ({ row }) => <div>{row.getValue("WarehouseID")}</div>,
  },
  {
    id: "partsCount",
    header: () => <div className="text-center">Parts</div>,
    cell: ({ row }) => (
      <div className="text-center">{row.original.parts.length}</div>
    ),
  },
  {
    accessorKey: "status",
    header: () => <div className="text-center">Status</div>,
    cell: ({ row }) => (
      <div className="flex justify-center">
        <StatusBadge status={row.getValue("status")} />
      </div>
    ),
  },
];

interface InventoryTransfersTableProps {
  data: InventoryTransferRecord[];
  emptyMessage?: string;
  filterPlaceholder?: string;
}

export function InventoryTransfersTable({
  data,
  emptyMessage = "No inventory transfers found.",
  filterPlaceholder = "Filter by Transfer No, TRX No, or site...",
}: InventoryTransfersTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "TranferDate", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = React.useState("");

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowCanExpand: () => true,
    globalFilterFn: (row, _columnId, filterValue) => {
      const query = String(filterValue).toLowerCase();
      if (!query) return true;

      const record = row.original;
      const haystack = [
        record.TransferNo,
        record.TRXNO,
        record.login_site,
        record.SalesmanNo,
        record.WarehouseID,
        record.from_warehouse,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    },
    initialState: {
      pagination: { pageSize: 25 },
    },
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  if (!data.length) {
    return (
      <p className="text-muted-foreground text-center py-10">{emptyMessage}</p>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <Input
          placeholder={filterPlaceholder}
          value={globalFilter}
          onChange={(event) => setGlobalFilter(event.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="p-0">
                        <PartsDetail record={row.original} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between py-4">
        <div className="text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} transfer(s)
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 25, 50, 100].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
